import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import { Helmet } from 'react-helmet-async';
import { Title } from './Title';
import { useCallback, useEffect, useState, useRef } from 'react';
import { fileNames, FileTypeError, getFileNames } from 'API';
import { Stack } from '@mui/material';
import { FilesSection, POLLING_INTERVAL, POLLING_LIMIT } from './FilesSection';
import { useNavigate } from 'react-router-dom';
import { RoutesConfig } from 'Routes';
import { Loader } from 'Components/Loader';
import { mandatoryFilesCategoryId } from './FilesSection';
import { ContinueRequest } from './ContinueRequest';

export const DocumentsHandle = () => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.DOCUMENTS_HANDLE);
  const [files, setFiles] = useState<fileNames>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isPollingFinished, setIsPollingFinished] = useState(false);
  const [checkedFiles, setCheckedFiles] = useState<Record<number, boolean>>({});
  const pollingCountRef = useRef(0);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const fetchFileNames = useCallback(async () => {
    const filesData = await getFileNames();
    if (filesData) {
      setFiles(filesData);
      return filesData;
    }
    return null;
  }, []);

  const initDocuments = useCallback(async () => {
    setIsLoading(true);
    const filesData = await fetchFileNames();
    setIsLoading(false);

    if (filesData) {
      const hasFilesToProcess = checkForUnprocessedFiles(filesData);

      if (hasFilesToProcess) {
        setIsPollingFinished(false);
        startPolling();
      } else {
        setIsPollingFinished(true);
      }
    }
  }, [fetchFileNames]);

  const checkForUnprocessedFiles = (filesData: fileNames): boolean => {
    if (pollingCountRef.current >= POLLING_LIMIT) {
      return false;
    }

    let hasUnprocessed = false;
    Object.values(filesData).forEach((categoryFiles) => {
      categoryFiles?.forEach((file) => {
        if (
          file.analysisErrorCode === FileTypeError.ServiceUnavailable ||
          file.analysisErrorCode === FileTypeError.UnknownError
        ) {
          return;
        }

        if (file.isAnalysisOccured === false) {
          hasUnprocessed = true;
        }
      });
    });

    return hasUnprocessed;
  };

  const startPolling = useCallback(() => {
    pollingCountRef.current = 0;

    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
    }

    pollForFileStatus();
  }, []);

  const pollForFileStatus = useCallback(async () => {
    pollingCountRef.current += 1;

    pollingTimerRef.current = setTimeout(async () => {
      const filesData = await fetchFileNames();

      if (filesData) {
        setFiles(filesData);

        const hasUnprocessed = checkForUnprocessedFiles(filesData);

        if (pollingCountRef.current < POLLING_LIMIT && hasUnprocessed) {
          pollForFileStatus();
        } else {
          pollingTimerRef.current = null;
          setIsPollingFinished(true);

          if (pollingCountRef.current >= POLLING_LIMIT) {
            const finalFilesData = await fetchFileNames();
            if (finalFilesData) {
              setFiles(finalFilesData);
            }
          }
        }
      }
    }, POLLING_INTERVAL);
  }, [fetchFileNames, checkForUnprocessedFiles]);

  const areAllFilesValid = useCallback(() => {
    if (Object.keys(files).length === 0) {
      return false;
    }

    return Object.values(files).every((categoryFiles) =>
      categoryFiles.every((file) => {
        const isFileValid =
          file.isAnalysisSucceeded ||
          file.analysisErrorCode === FileTypeError.ServiceUnavailable ||
          file.analysisErrorCode === FileTypeError.UnknownError ||
          (pollingCountRef.current >= POLLING_LIMIT &&
            file.isAnalysisOccured === false);

        return isFileValid;
      })
    );
  }, [files]);

  const areMandatoryFilesValid = useCallback(() => {
    if (Object.keys(files).length === 0) return false;
    return Object.values(files).every((categoryFiles) =>
      categoryFiles.every((file) => {
        const isMandatory = mandatoryFilesCategoryId.includes(
          file.fileCategoryRelationId
        );
        if (!isMandatory) return true;

        return (
          file.isAnalysisSucceeded ||
          file.analysisErrorCode === FileTypeError.ServiceUnavailable ||
          file.analysisErrorCode === FileTypeError.UnknownError ||
          (pollingCountRef.current >= POLLING_LIMIT &&
            file.isAnalysisOccured === false) ||
          checkedFiles[file.id]
        );
      })
    );
  }, [files, checkedFiles]);

  const handleContinue = useCallback(() => {
    navigate(RoutesConfig.VERIFY_DETAILS.PATH);
  }, [navigate]);

  useEffect(() => {
    if (!isLoading && Object.keys(files).length > 0 && areAllFilesValid()) {
      handleContinue();
    }
  }, [isLoading, files, areAllFilesValid, handleContinue]);

  useEffect(() => {
    initDocuments();
    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
      }
    };
  }, [initDocuments]);

  const hasUnprocessedFilesInCategory = (isMandatory: boolean): boolean => {
    if (pollingCountRef.current >= POLLING_LIMIT) {
      return false;
    }

    let hasUnprocessed = false;

    Object.values(files).forEach((categoryFiles) => {
      categoryFiles?.forEach((file) => {
        const fileIsMandatory = mandatoryFilesCategoryId.includes(
          file.fileCategoryRelationId
        );
        if (
          file.isAnalysisOccured === false &&
          fileIsMandatory === isMandatory
        ) {
          hasUnprocessed = true;
        }
      });
    });

    return hasUnprocessed;
  };

  const handleCheckboxChange = (fileId: number, checked: boolean) => {
    setCheckedFiles((prev) => ({ ...prev, [fileId]: checked }));
  };

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <Helmet>
        <title>{dictionary.title}</title>
      </Helmet>
      <Title />
      <Stack
        gap='64px'
        sx={{
          margin: '48px auto 0 auto',
          maxWidth: '780px',
        }}
      >
        <FilesSection
          files={files}
          onFileUpdate={initDocuments}
          hasUnprocessedMandatoryFiles={hasUnprocessedFilesInCategory(true)}
          hasUnprocessedOptionalFiles={hasUnprocessedFilesInCategory(false)}
          pollingCount={pollingCountRef.current}
          checkedFiles={checkedFiles}
          onCheckboxChange={handleCheckboxChange}
        />
      </Stack>
      {isPollingFinished && (
        <ContinueRequest
          getFilesList={initDocuments}
          isValid={areMandatoryFilesValid()}
        />
      )}
    </>
  );
};
